import { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Loader2, Image, AlertCircle, Check } from 'lucide-react';
import activitiesApi from '../../services/activitiesApi.js';

const SLOTS = [
  { key: 'logo', label: 'Logo', description: 'Logo principale dell\'attività. Usato su tutti i servizi.' },
  { key: 'logo-dark', label: 'Logo (scuro)', description: 'Variante scura del logo, per sfondi chiari.' },
  { key: 'cover', label: 'Copertina', description: 'Immagine di copertina per Page e altri servizi.' },
];

export default function ActivityMediaManager({ activityId }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null); // slot name or 'gallery'
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const galleryInputRef = useRef(null);

  const loadMedia = async () => {
    try {
      const result = await activitiesApi.getMedia(activityId);
      if (result.success) {
        setMedia(result.data.media || []);
      }
    } catch (err) {
      setError('Errore nel caricamento dei media');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activityId) loadMedia();
  }, [activityId]);

  const handleUpload = async (file, slot) => {
    if (!file) return;

    setUploading(slot);
    setError('');
    setSuccess('');

    try {
      const result = await activitiesApi.uploadMedia(activityId, file, slot);
      if (result.success) {
        setSuccess(`${slot === 'gallery' ? 'Immagine' : SLOTS.find(s => s.key === slot)?.label || slot} caricato`);
        await loadMedia();
      } else {
        setError(result.error || 'Errore durante l\'upload');
      }
    } catch (err) {
      setError('Errore durante l\'upload');
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (filePath) => {
    if (!confirm('Eliminare questa immagine?')) return;

    setError('');
    try {
      const result = await activitiesApi.deleteMedia(activityId, filePath);
      if (result.success) {
        setSuccess('Immagine eliminata');
        await loadMedia();
      } else {
        setError(result.error || 'Errore durante l\'eliminazione');
      }
    } catch (err) {
      setError('Errore durante l\'eliminazione');
    }
  };

  const getMediaForSlot = (slot) => {
    return media.filter(m => m.slot === slot);
  };

  const galleryMedia = media.filter(m => m.slot === 'gallery');

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
          <Image className="w-5 h-5 text-primary-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Media</h2>
          <p className="text-sm text-gray-500">Logo e immagini condivisi su tutti i servizi</p>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-100 rounded-lg flex items-start space-x-2">
          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}

      {/* Slot principali */}
      <div className="space-y-4 mb-6">
        {SLOTS.map(slot => {
          const items = getMediaForSlot(slot.key);
          const isUploading = uploading === slot.key;

          return (
            <SlotSection
              key={slot.key}
              slot={slot}
              items={items}
              isUploading={isUploading}
              onUpload={(file) => handleUpload(file, slot.key)}
              onDelete={(filePath) => handleDelete(filePath)}
            />
          );
        })}
      </div>

      {/* Gallery */}
      <div className="border-t border-gray-100 pt-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-700">Galleria</h3>
          <label className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg cursor-pointer hover:bg-primary-100 transition-colors">
            {uploading === 'gallery' ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : (
              <Upload className="w-3 h-3 mr-1" />
            )}
            Aggiungi
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/svg+xml"
              className="hidden"
              onChange={(e) => {
                if (e.target.files[0]) handleUpload(e.target.files[0], 'gallery');
                e.target.value = '';
              }}
              disabled={uploading === 'gallery'}
            />
          </label>
        </div>

        {galleryMedia.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">Nessuna immagine in galleria</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {galleryMedia.map(item => (
              <div key={item.path || item.name} className="group relative rounded-lg overflow-hidden border border-gray-200">
                <img
                  src={item.url}
                  alt={item.name}
                  className="w-full h-24 object-cover"
                />
                <button
                  onClick={() => handleDelete(item.path || `gallery/${item.name}`)}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Elimina"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SlotSection({ slot, items, isUploading, onUpload, onDelete }) {
  const inputRef = useRef(null);

  return (
    <div className="border border-gray-200 rounded-xl p-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <span className="text-sm font-medium text-gray-900">{slot.label}</span>
          <p className="text-xs text-gray-400 mt-0.5">{slot.description}</p>
        </div>
        <label className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg cursor-pointer hover:bg-primary-100 transition-colors">
          {isUploading ? (
            <Loader2 className="w-3 h-3 animate-spin mr-1" />
          ) : (
            <Upload className="w-3 h-3 mr-1" />
          )}
          Aggiungi
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/svg+xml"
            className="hidden"
            onChange={(e) => {
              if (e.target.files[0]) onUpload(e.target.files[0]);
              e.target.value = '';
            }}
            disabled={isUploading}
          />
        </label>
      </div>

      {items.length === 0 ? (
        <label className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-primary-300 hover:bg-primary-50/30 transition-colors">
          {isUploading ? (
            <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
          ) : (
            <>
              <Upload className="w-5 h-5 text-gray-400 mb-1" />
              <span className="text-xs text-gray-400">Carica</span>
            </>
          )}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/svg+xml"
            className="hidden"
            onChange={(e) => {
              if (e.target.files[0]) onUpload(e.target.files[0]);
              e.target.value = '';
            }}
            disabled={isUploading}
          />
        </label>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {items.map(item => (
            <div key={item.path || item.name} className="group relative rounded-lg overflow-hidden border border-gray-200">
              <img
                src={`${item.url}?t=${Date.now()}`}
                alt={slot.label}
                className="w-full h-28 object-contain bg-gray-50"
              />
              <button
                onClick={() => onDelete(item.path || item.name)}
                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                title="Elimina"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
